import { createSelector, Dispatch } from '@reduxjs/toolkit';
import { selectTextForTextSearch } from 'store/slice/home';
import { selectConfigFromBootstrap, selectFeatureBehaviorFromBootstrap, selectLoyaltySearchTypeFromBootstrapState, setLoyaltySearchType } from 'store/slice/bootstrap';
import { BootstrapConfig } from 'core/type/BootstrapConfig.types';
import { selectFilteredActivitiesFromSearchState, selectFilterState, selectMatrixFromSearchState, selectSearchState } from 'component/page/search/store/search.selectors';
import { fetchActivities } from 'component/page/search/store/search';
import { LoyaltySearchType } from 'core/type/LoyaltySearch.types';
import { ActivitiesBehavior } from 'core/type/behaviors.types';
import { ReactElement } from 'react';
import { ScreenSize, selectScreenSize } from 'store/slice/screen';
import { isCityPage, isSearchPage } from 'core/constant/analytics/AnalyticsPageType';
import { FilterMatrix } from 'gql/autogen/FilterMatrix';
import { FilterState } from 'component/common/Filter/Filter.types';
import { isOn } from 'core/window/bootstrapConfig';
import { FeatureName } from 'core/constant/FeatureName';

interface StateProps {
    cityName: string;
    config: BootstrapConfig;
    isLoading: boolean;
    isSuccess: boolean;
    totalActivities: number;
    behavior: ActivitiesBehavior;
    loyaltySearchType: LoyaltySearchType;
    screenSize: ScreenSize;
    isInCitySearch: boolean;
    filter: FilterState;
    filterMatrix: FilterMatrix;
    isPageContainerSpacing: boolean;
    totalFilteredActivities: number;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface DispatchProps {
    setLoyaltySearchType: (loyaltySearchType: LoyaltySearchType) => void;
}

interface OwnProps {
    isNoResults: boolean;
    isEmbedded?: boolean;
    embeddedFooter?: ReactElement;
    searchPagePadding?: boolean;
    isKeywordDisplayed?: boolean;
}

export type SearchPageContentProps = StateProps & DispatchProps & OwnProps;

export const mapStateToProps = createSelector(
    selectConfigFromBootstrap,
    selectTextForTextSearch,
    selectSearchState,
    selectFilteredActivitiesFromSearchState,
    selectLoyaltySearchTypeFromBootstrapState,
    selectScreenSize,
    selectFeatureBehaviorFromBootstrap,
    selectFilterState,
    selectMatrixFromSearchState,
    (config, searchText, state, totalActivities, loyaltySearchType, screenSize, featureBehaviours, filter, matrix): StateProps => {
        const cityName = searchText;
        const behavior = config.behavior;
        const filterMatrix = matrix?.filter || {};
        const pageTypeId = config.pageConfig.pageTypeId;
        const isPageContainerSpacing = isOn(FeatureName.PageContainerSpacing, config);
        const totalFilteredActivities = state.searchResponse?.result?.resultInfo?.totalFilteredActivities || 0;
        return {
            config,
            cityName,
            totalActivities,
            behavior,
            loyaltySearchType,
            screenSize,
            filter,
            filterMatrix,
            isLoading: state.isLoading,
            isSuccess: state.isSuccess && !state.isLoading,
            isInCitySearch: (isSearchPage(pageTypeId) || isCityPage(pageTypeId)) && featureBehaviours.SearchBoxFeatureGroup,
            isPageContainerSpacing,
            totalFilteredActivities,
        };
    },
);

export const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
    setLoyaltySearchType: (loyaltySearchType: LoyaltySearchType) => {
        dispatch(setLoyaltySearchType(loyaltySearchType));
        fetchActivities();
    },
});
